(() => {
  const panel = document.querySelector('[data-rbx-sp]');
  const wrapper = document.querySelector('.header__search-bar-wrapper');
  const input = wrapper?.querySelector('input[name="q"]');
  if (!panel || !input) return;

  const DEBOUNCE = 200;
  const header = panel.closest('.shopify-section') || panel.parentElement;
  const backdrop = document.querySelector('[data-rbx-sp-backdrop]');
  const phone = window.matchMedia('(max-width: 640px)');
  const slots = {
    suggestions: panel.querySelector('[data-rbx-sp-slot="suggestions"]'),
    articles: panel.querySelector('[data-rbx-sp-slot="articles"]'),
    items: panel.querySelector('[data-rbx-sp-slot="items"]'),
  };
  const suggestionsLabel = panel.querySelector('[data-rbx-sp-suggestions-label]');
  const articlesBox = panel.querySelector('[data-rbx-sp-articles]');
  const itemsLabel = panel.querySelector('[data-rbx-sp-items-label]');
  const empty = panel.querySelector('[data-rbx-sp-empty]');
  const queryRow = panel.querySelector('[data-rbx-sp-query]');
  const ask = panel.querySelector('[data-rbx-sp-ask]');
  const arrows = panel.querySelector('[data-rbx-sp-arrows]');
  const prev = panel.querySelector('[data-rbx-sp-prev]');
  const next = panel.querySelector('[data-rbx-sp-next]');
  const askBase = ask ? ask.getAttribute('href') : '';
  const popularSuggestions = slots.suggestions.innerHTML;
  const cache = new Map();

  let isOpen = false;
  let barOpen = false;
  let timer = null;
  let controller = null;
  let current = null;
  let active = -1;
  let optionId = 0;

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', panel.id);
  input.setAttribute('aria-expanded', 'false');

  const urlFor = (query) => {
    if (!query) return `${panel.dataset.popularUrl}?section_id=${panel.dataset.sectionId}`;
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query,article',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: panel.dataset.sectionId,
    });
    return `${panel.dataset.suggestUrl}?${params}`;
  };

  const searchUrl = (query) => `${panel.dataset.searchUrl}?${new URLSearchParams({ q: query, type: 'product' })}`;

  const options = () => [...panel.querySelectorAll('[data-rbx-sp-option]')].filter((el) => !el.closest('[hidden]') && el.getClientRects().length);

  const setActive = (index) => {
    const list = options();
    list[active]?.classList.remove('is-active');
    active = index;
    const option = list[active];
    if (!option) {
      active = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }
    if (!option.id) option.id = `rbx-sp-option-${(optionId += 1)}`;
    option.classList.add('is-active');
    input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const updateArrows = () => {
    const track = slots.items;
    const overflows = track.scrollWidth > track.clientWidth + 1;
    arrows.hidden = !overflows;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
  };

  // The panel sits under the header; on phones it hangs off the dropped-down search row instead, which covers the nav bar.
  const place = () => {
    let top = header.getBoundingClientRect().bottom;
    if (phone.matches && barOpen) top = wrapper.getBoundingClientRect().bottom;
    panel.style.setProperty('--rbx-sp-top', `${Math.max(0, top)}px`);
  };

  const showStatic = (query) => {
    if (ask) ask.href = query ? `${askBase}${askBase.includes('?') ? '&' : '?'}q=${encodeURIComponent(query)}` : askBase;
    queryRow.hidden = !query;
    queryRow.href = searchUrl(query);
    queryRow.querySelector('[data-rbx-sp-query-text]').textContent = query;
    itemsLabel.textContent = query ? itemsLabel.dataset.results : itemsLabel.dataset.popular;
  };

  const apply = (data, query) => {
    panel.classList.remove('is-loading');
    setActive(-1);
    slots.suggestions.innerHTML = query ? data.suggestions : popularSuggestions;
    suggestionsLabel.hidden = !slots.suggestions.children.length;
    slots.articles.innerHTML = query ? data.articles : '';
    articlesBox.hidden = !slots.articles.children.length;
    slots.items.innerHTML = data.items;
    slots.items.scrollLeft = 0;
    const noItems = !slots.items.children.length;
    slots.items.hidden = noItems;
    itemsLabel.hidden = noItems && !query;
    empty.hidden = !(noItems && query);
    empty.querySelector('[data-rbx-sp-empty-text]').textContent = query;
    updateArrows();
  };

  const parse = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const part = (name) => doc.querySelector(`[data-rbx-sp-part="${name}"]`)?.innerHTML.trim() || '';
    return { suggestions: part('suggestions'), articles: part('articles'), items: part('items') };
  };

  const render = (query) => {
    current = query;
    showStatic(query);
    const url = urlFor(query);
    controller?.abort();
    if (cache.has(url)) {
      apply(cache.get(url), query);
      return;
    }
    controller = new AbortController();
    panel.classList.add('is-loading');
    fetch(url, { signal: controller.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Search panel request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const data = parse(html);
        cache.set(url, data);
        if (current === query) apply(data, query);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        panel.classList.remove('is-loading');
        if (current === query) apply({ suggestions: '', articles: '', items: '' }, query);
      });
  };

  const open = () => {
    if (isOpen) return;
    isOpen = true;
    place();
    requestAnimationFrame(place);
    panel.classList.add('is-open');
    backdrop?.classList.add('is-visible');
    document.documentElement.classList.add('rbx-sp-locked');
    input.setAttribute('aria-expanded', 'true');
    render(input.value.trim());
  };

  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    clearTimeout(timer);
    controller?.abort();
    panel.classList.remove('is-open', 'is-loading');
    backdrop?.classList.remove('is-visible');
    document.documentElement.classList.remove('rbx-sp-locked');
    input.setAttribute('aria-expanded', 'false');
    setActive(-1);
  };

  // Phones: the header icon drops the search row down under the logo row. Results only appear once there is a query.
  const openBar = () => {
    barOpen = true;
    wrapper.classList.add('rbx-sb-open');
    input.focus();
    if (input.value.trim()) open();
  };

  // Clearing the query means the row always reopens empty, with no results until the next keystroke
  const closeBar = () => {
    barOpen = false;
    close();
    wrapper.classList.remove('rbx-sb-open');
    input.value = '';
    input.blur();
  };

  const openIfQuery = () => {
    if (!phone.matches || input.value.trim()) open();
  };

  const onInput = () => {
    clearTimeout(timer);
    const query = input.value.trim();
    if (phone.matches && !query) {
      close();
      return;
    }
    open();
    if (query === current) return;
    timer = setTimeout(() => render(query), DEBOUNCE);
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault();
        close();
      }
      return;
    }
    if (!isOpen) {
      if (event.key === 'ArrowDown') open();
      return;
    }
    const count = options().length;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!count) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = active + step;
      setActive(nextIndex < -1 ? count - 1 : nextIndex >= count ? -1 : nextIndex);
    } else if (event.key === 'Enter' && active > -1) {
      const option = options()[active];
      if (option) {
        event.preventDefault();
        window.location.href = option.href;
      }
    } else if (event.key === 'Tab' && !event.shiftKey && count) {
      event.preventDefault();
      options()[0].focus();
    }
  };

  // theme.js's SearchBar listens for input and keydown on the wrapper to fetch and focus its old dropdown.
  // Stopping both at the input keeps that dropdown idle. On phones its focus handling is stopped too, because it turns the bar into a full-screen layer.
  input.addEventListener('input', (event) => {
    event.stopPropagation();
    onInput();
  });
  input.addEventListener('keydown', (event) => {
    event.stopPropagation();
    onKeydown(event);
  });
  input.addEventListener('focusin', (event) => {
    if (phone.matches) event.stopPropagation();
  });
  input.addEventListener('focus', openIfQuery);
  input.addEventListener('click', openIfQuery);

  wrapper.querySelector('[data-action="clear-input"]')?.addEventListener('click', () => {
    setTimeout(() => {
      input.focus();
      if (phone.matches) close();
      else render('');
    });
  });
  wrapper.querySelector('[data-action="unfix-search"]')?.addEventListener('click', () => (barOpen ? closeBar() : close()));

  // On phones the header icon toggles the search row. theme.js's own toggle grows the header and never resets its open flag on Close,
  // so it is stopped here, in capture before its delegate on the header. The CSS makes the row visible in the same frame, so the
  // input is focusable inside the tap, which iOS needs to raise the keyboard.
  const isToggle = (target) => !!target.closest?.('[data-action="toggle-search"]');

  header.addEventListener('click', (event) => {
    if (!phone.matches || !isToggle(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    if (barOpen) closeBar();
    else openBar();
  }, true);

  phone.addEventListener('change', () => barOpen && closeBar());

  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      input.focus({ preventScroll: true });
    } else if (event.key === 'Tab' && event.shiftKey && document.activeElement === options()[0]) {
      event.preventDefault();
      input.focus({ preventScroll: true });
    }
  });

  const dismiss = (event) => {
    if (panel.contains(event.target) || wrapper.contains(event.target)) return;
    if (barOpen) {
      if (!isToggle(event.target)) closeBar();
    } else if (isOpen) {
      close();
    }
  };

  document.addEventListener('focusin', dismiss);
  document.addEventListener('pointerdown', dismiss, true);

  // The backdrop is not focusable, so a tap on it would otherwise blur the input before close() runs.
  backdrop?.addEventListener('mousedown', (event) => event.preventDefault());

  prev.addEventListener('click', () => slots.items.scrollBy({ left: -slots.items.clientWidth * 0.8 }));
  next.addEventListener('click', () => slots.items.scrollBy({ left: slots.items.clientWidth * 0.8 }));
  slots.items.addEventListener('scroll', updateArrows, { passive: true });

  wrapper.addEventListener('transitionend', () => isOpen && place());

  window.addEventListener('resize', () => {
    if (!isOpen) return;
    place();
    updateArrows();
  }, { passive: true });
  window.addEventListener('scroll', () => isOpen && place(), { passive: true });
})();
